import { useEffect, useMemo, useState } from "react";
import {
  Link,
  useLocation,
  useNavigate,
} from "react-router-dom";

import Api from "../../../shared/api/api";
import useCheckoutResult from "../../../shared/hooks/useCheckoutResult";
import "../styles/ShopPage.css";

const PRODUCT_CATEGORIES = [
  "Apparel",
  "Glassware",
  "Accessories",
];

function getFrontImage(product) {
  const productImages = Array.isArray(product?.images)
    ? product.images
    : [];

  const frontImage = productImages.find((image) => {
    const imageUrl = String(image?.image_url || "").toLowerCase();

    return (
      imageUrl.includes("camera_label=front") ||
      imageUrl.includes("front")
    );
  });

  return (
    frontImage?.image_url ||
    product?.image_url ||
    productImages[0]?.image_url ||
    null
  );
}

function getProductCategory(product) {
  const category = String(product?.category || "")
    .trim()
    .toLowerCase();

  if (category === "apparel") {
    return "Apparel";
  }

  if (category === "glassware") {
    return "Glassware";
  }

  return "Accessories";
}

export default function ShopPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [productStatus, setProductStatus] = useState("loading");
  const checkoutStatus = useCheckoutResult(location.search);
  const [loadedImages, setLoadedImages] = useState({});

  useEffect(() => {
    let cancelled = false;

    async function fetchProducts() {
      try {
        setProductStatus("loading");

        const response = await Api.getMerchProducts();

        if (cancelled) return;

        const productRecords = Array.isArray(response)
          ? response
          : response?.products || response?.data || [];

        setProducts(
          Array.isArray(productRecords) ? productRecords : []
        );

        setProductStatus("success");
      } catch (error) {
        if (cancelled) return;

        console.error("Error loading merchandise:", error);

        setProducts([]);
        setProductStatus("error");
      }
    }

    fetchProducts();

    return () => {
      cancelled = true;
    };
  }, []);

  const groupedProducts = useMemo(() => {
    const groups = {
      Apparel: [],
      Glassware: [],
      Accessories: [],
    };

    products.forEach((product) => {
      const category = getProductCategory(product);

      groups[category].push(product);
    });

    Object.values(groups).forEach((items) => {
      items.sort((firstProduct, secondProduct) =>
        String(firstProduct?.title || "").localeCompare(
          String(secondProduct?.title || "")
        )
      );
    });

    return groups;
  }, [products]);

  function markImageLoaded(productKey) {
    setLoadedImages((currentImages) => ({
      ...currentImages,
      [productKey]: true,
    }));
  }

  function renderProductSection(title, items) {
    if (items.length === 0) {
      return null;
    }

    const sectionId = `shop-${title.toLowerCase()}-heading`;

    return (
      <section
        className="shop-section"
        key={title}
        aria-labelledby={sectionId}
      >
        <h2 id={sectionId} className="shop-section-title">
          {title}
        </h2>

        <div className="shop-grid">
          {items.map((product) => {
            const productKey =
              product?.id ||
              product?._id ||
              product?.printify_product_id ||
              product?.title;

            const productUrl = `/shop/p/${encodeURIComponent(
              product?.id || product?._id || ""
            )}`;

            const productImage = getFrontImage(product);
            const imageHasLoaded = Boolean(
              loadedImages[productKey]
            );

            return (
              <article className="shop-card" key={productKey}>
                {productImage ? (
                  <Link
                    to={productUrl}
                    state={{ product }}
                    className="shop-card-image-wrapper"
                    aria-label={`View ${product?.title || "product"}`}
                  >
                    {!imageHasLoaded && (
                      <div
                        className="image-placeholder"
                        aria-hidden="true"
                      />
                    )}

                    <img
                      src={productImage}
                      alt={product?.title || "Merchandise product"}
                      className={`shop-card-image ${
                        imageHasLoaded ? "loaded" : ""
                      }`}
                      onLoad={() => markImageLoaded(productKey)}
                      onError={() => markImageLoaded(productKey)}
                      loading="lazy"
                      decoding="async"
                    />
                  </Link>
                ) : (
                  <div className="shop-card-image-wrapper">
                    <div className="image-placeholder">
                      No image available
                    </div>
                  </div>
                )}

                <div className="shop-card-content">
                  <h3 className="shop-card-title">
                    {product?.title || "Untitled Product"}
                  </h3>

                  {product?.price != null && (
                    <p className="shop-card-price">
                      ${Number(product.price).toFixed(2)}
                    </p>
                  )}

                  {(product?.id || product?._id) && (
                    <Link
                      to={productUrl}
                      state={{ product }}
                      className="shop-card-button"
                    >
                      View Product
                    </Link>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </section>
    );
  }

  return (
    <main className="shop-page-container">
      {checkoutStatus === "success" && (
        <div
          className="checkout-success-message"
          role="status"
          aria-live="polite"
        >
          Thank you! Your order was placed.
        </div>
      )}

      {(checkoutStatus === "verifying" || checkoutStatus === "processing") && (
        <div className="checkout-success-message" role="status" aria-live="polite">
          {checkoutStatus === "verifying"
            ? "Confirming your order..."
            : "Payment received. Your order is still processing; your confirmation email will arrive shortly."}
        </div>
      )}

      {checkoutStatus === "error" && (
        <div className="checkout-cancel-message" role="alert">
          We could not verify that payment was completed. Your cart has been preserved.
        </div>
      )}

      {checkoutStatus === "cancel" && (
        <div
          className="checkout-cancel-message"
          role="status"
          aria-live="polite"
        >
          Checkout was canceled.
        </div>
      )}

      <h1 className="shop-page-title">Shop Merchandise</h1>

      {productStatus === "loading" && (
        <p className="loadingMsg" role="status">
          Loading merchandise...
        </p>
      )}

      {productStatus === "error" && (
        <p className="warningError" role="alert">
          Merchandise could not be loaded. Please try again later.
        </p>
      )}

      {productStatus === "success" && products.length === 0 && (
        <p className="shop-empty-message">
          No merchandise is currently available.
        </p>
      )}

      {productStatus === "success" &&
        products.length > 0 &&
        PRODUCT_CATEGORIES.map((category) =>
          renderProductSection(
            category,
            groupedProducts[category]
          )
        )}
    </main>
  );
}
